<template>
  <v-table
    :count="count"
    :list="list"
    :is-loading="isLoading"
    :push-list="refresh"
    default-order-by="start_time"
    :query-list="queryList"
    :is-show-store-select="isHeadOffice"
    :is-store-select-clearable="true"
    :is-show-quick-time-tab="true"
    :is-show-quick-time-tab-next-week="true"
    :is-show-teacher-select="true"
    :is-show-select-card-type-id="true"
    :is-show-select-course-column-id="true"
    :show-summary="true"
    :picker-options="pickerOptionsOfAfterSystemStartDay"
    :summary-title="isPc ? '大课平均人数' : '均数'"
    :row-class-name="tableRowClassName"
  >
    <el-table-column type="expand" v-if="!isPc">
      <template v-slot="{ row }">
        <el-form label-position="left" inline class="table-expand">
          <el-form-item label="添加时间">
            <span>{{ row.add_time | timeToYmdHi }}</span>
          </el-form-item>

          <el-form-item label="开始时间">
            <span>{{ row.start_time | timeToYmdHi }}</span>
          </el-form-item>

          <el-form-item label="结束时间">
            <span>{{ row.end_time | timeToYmdHi }}</span>
          </el-form-item>

          <el-form-item label="课程类型">
            <span>{{ row.card_type_name }}</span>
          </el-form-item>

          <el-form-item label="已预约">
            <span>{{ row.student_count }}</span>
          </el-form-item>

          <el-form-item label="试课">
            <span>{{ row.trial_count }}</span>
          </el-form-item>

          <el-form-item label="排队中">
            <span>{{ row.wait_count }}</span>
          </el-form-item>

          <el-form-item label="已打卡">
            <span>{{ row.signed_count }}</span>
          </el-form-item>
        </el-form>
      </template>
    </el-table-column>
    <!-- 日期与时段分两行、都常驻：不按筛选范围猜，所以「今天」/「全部」/跨年都不会歧义。
         日期行最长「2026-09-21 次日」≈94px（12px 字号）、时段行「周一 23:30 - 00:30」≈119px，
         都短于 150 列的内容区（130px），所以恒为两行、不可能折成三行。宽度 250 → 130 → 150（新增周几） -->
    <el-table-column label="课程时间" width="150">
      <template v-slot="{ row }">
        <div class="course-time-date">{{ formatCourseDate(row.start_time, row.end_time) }}</div>
        <div class="course-time-range">{{ formatCourseTimeRange(row.start_time, row.end_time) }}</div>
      </template>
    </el-table-column>
    <el-table-column v-if="isHeadOffice" label="课程门店" prop="store_name"/>
    <!-- 老师列出勤状态标签已只显示异常，500 → 380 就够了；省下的宽度给「课程名称」 -->
    <el-table-column label="课程老师/出勤状态" width="380">
      <template v-slot="{ row }">

        <div class="teacher-item" v-for="(teacher, index) in row.teachers" :key="`course${index}`">
          <v-avatar-nickname
            :value="teacher.avatar"
            :nickname="teacher.nickname"
            :is-center="true"
            :nickname-overflow="true"
            :link-page-name="RouteConfig.teacherDetail.name"
            :link-page-id="teacher.teacher_id"
            :avatar-size="50"
          />
          <el-tag
            v-for="v in findAbnormalCourseState(teacher.course_state)"
            :key="`teacherState${v.name}`"
            :type="v.tagType"
          >
            {{ v.name }}
          </el-tag>
          <template v-if="teacher.sub_teacher_id">
            <i class="icon-arrow-right" />
            <v-avatar-nickname
              :value="teacher.sub_avatar"
              :nickname="teacher.sub_nickname"
              :is-center="true"
              :nickname-overflow="true"
              :link-page-name="RouteConfig.teacherDetail.name"
              :link-page-id="teacher.sub_teacher_id"
              :avatar-size="50"
            />
            <el-tag
              v-for="v in findAbnormalCourseState(teacher.sub_course_state)"
              :key="`subTeacherState${v.name}`"
              :type="v.tagType"
            >
              {{ v.name }}
            </el-tag>
          </template>
        </div>

      </template>
    </el-table-column>
    <!-- 课名单行显示、超长省略并悬浮看全（原先只有 ~100px，会折成两三行把行高撑高） -->
    <el-table-column label="课程名称" prop="course_name" min-width="220" show-overflow-tooltip/>
    <!-- 预告片列按 100px 计：1080p 容器约 1594px（85vw 减 1vw×2 内边距），整表最小宽度已接近上限，
         再加列需同步收紧其它列，否则会出现横向滚动条 -->
    <el-table-column label="预告片" width="100">
      <template v-slot="{ row }">
        <el-tag v-for="state in findVideoState(row.trailer_state)" :key="`trailerState${state.name}`" :type="state.tagType">
          {{ state.name }}
        </el-tag>
        <el-tag v-if="row.trailer_state < 0" type="info">未上传</el-tag>
      </template>
    </el-table-column>
    <!-- 「常规班成人团课」7 个字在 14px 下约 118px，给足 120 才不会被截成省略号 -->
    <el-table-column label="课程类型" prop="card_type_name" width="120" v-if="isPc" show-overflow-tooltip/>
    <el-table-column label="已预约" prop="student_count" type="average"/>
    <!-- 1080p 一行放不下：试课/排队中只在 2K 屏展示（同 daily-report 的 is2k 用法），避免整表出现横向滚动条 -->
    <el-table-column label="试课" prop="trial_count" type="average" v-if="is2k"/>
    <el-table-column label="排队中" prop="wait_count" type="average" v-if="is2k"/>
    <el-table-column label="已打卡" prop="signed_count" type="average"/>
    <el-table-column label="操作" width="250">
      <template v-slot="{ row }">

        <v-list-button :to="RouteConfig.courseEdit.to(row.id)"/>
        <v-list-button icon="signed-in" name="签到" :to="RouteConfig.courseReserve.to(row.id)"/>

        <v-popover
          name="删除"
          @click="handleDelete(row)"
          :is-loading="isDeleting[row.id]"
          message="小程序将下线该课程，但不会自动取消预约，确定要继续操作吗？"
        >
          <v-popover-item icon="copy" name="复制模板" :to="RouteConfig.courseAdd.to('', { id:  row.id })"/>
          <v-popover-item
            icon="mini-app-qr-code"
            name="小程序码"
            @click="getQrCode(row.id, true)"
            :is-loading="isGetting[row.id]"
          />
          <v-popover-item icon="trial" name="添加试课" :to="RouteConfig.courseTrial.to(row.id)"/>
          <v-popover-item icon="reserve-manage" name="预约管理" :to="RouteConfig.courseManage.to(row.id)"/>
        </v-popover>
      </template>
    </el-table-column>


    <template slot="mobile-list">
      <section class="list">
        <template v-if="list.length">
          <div
            v-for="(v, k) in list"
            :key="`course-list-${k}-${v.id}`"
            class="list-item primary-background-light"
          >
            <div class="list-item-detail">
              <span>课程时间</span>
              <!-- 原先这里取的是 v.add_time（创建时间），手机端看到的"上课时间"是错的 -->
              <span>{{ formatCourseDate(v.start_time, v.end_time) }} {{ formatCourseTimeRange(v.start_time, v.end_time) }}</span>
            </div>

            <div class="list-item-detail">
              <span v-if="isHeadOffice">课程门店</span>
              <span>{{ v.store_name }}</span>
            </div>

            <div class="list-item-detail">
              <span>课程老师/出勤状态</span>
              <div class="teacher-item" v-for="(teacher, index) in v.teachers" :key="`course${index}`">
                <v-avatar-nickname
                  :value="teacher.avatar"
                  :nickname="teacher.nickname"
                  :is-center="true"
                  :nickname-overflow="true"
                  :link-page-name="RouteConfig.teacherDetail.name"
                  :link-page-id="teacher.teacher_id"
                  :avatar-size="50"
                />
                <el-tag
                  v-for="v in findAbnormalCourseState(teacher.course_state)"
                  :key="`teacherState${v.name}`"
                  :type="v.tagType"
                >
                  {{ v.name }}
                </el-tag>
                <template v-if="teacher.sub_teacher_id">
                  <i class="icon-arrow-right" />
                  <v-avatar-nickname
                    :value="teacher.sub_avatar"
                    :nickname="teacher.sub_nickname"
                    :is-center="true"
                    :nickname-overflow="true"
                    :link-page-name="RouteConfig.teacherDetail.name"
                    :link-page-id="teacher.sub_teacher_id"
                    :avatar-size="50"
                  />
                  <el-tag
                    v-for="v in findAbnormalCourseState(teacher.sub_course_state)"
                    :key="`subTeacherState${v.name}`"
                    :type="v.tagType"
                  >
                    {{ v.name }}
                  </el-tag>
                </template>
              </div>
            </div>

            <div class="list-item-detail">
              <span>课程名称</span>
              <span>{{ v.course_name }}</span>
            </div>

            <div class="list-item-detail">
              <span>预告片</span>
              <el-tag v-for="state in findVideoState(v.trailer_state)" :key="`trailerState${state.name}`" :type="state.tagType">
                {{ state.name }}
              </el-tag>
              <el-tag v-if="v.trailer_state < 0" type="info">未上传</el-tag>
            </div>

            <div class="list-item-detail">
              <span>课程类型</span>
              <span>{{ v.card_type_name }}</span>
            </div>

            <div class="list-item-detail">
              <span>已预约</span>
              <span>{{ v.student_count }}</span>
            </div>

            <div class="list-item-detail">
              <span>试课</span>
              <span>{{ v.trial_count }}</span>
            </div>

            <div class="list-item-detail">
              <span>排队中</span>
              <span>{{ v.wait_count }}</span>
            </div>

            <div class="list-item-detail">
              <span>已打卡</span>
              <span>{{ v.signed_count }}</span>
            </div>

            <div class="list-item-button">
              <v-list-button :to="RouteConfig.courseEdit.to(v.id)"/>
              <v-list-button icon="signed-in" name="签到" :to="RouteConfig.courseReserve.to(v.id)"/>

              <v-popover
                name="删除"
                @click="handleDelete(v)"
                :is-loading="isDeleting[v.id]"
                message="小程序将下线该课程，但不会自动取消预约，确定要继续操作吗？"
              >
                <v-popover-item icon="copy" name="复制模板" :to="RouteConfig.courseAdd.to('', { id:  v.id })"/>
                <v-popover-item
                  icon="mini-app-qr-code"
                  name="小程序码"
                  @click="getQrCode(v.id, true)"
                  :is-loading="isGetting[v.id]"
                />
                <v-popover-item icon="trial" name="添加试课" :to="RouteConfig.courseTrial.to(v.id)"/>
                <v-popover-item icon="reserve-manage" name="预约管理" :to="RouteConfig.courseManage.to(v.id)"/>
              </v-popover>
            </div>
          </div>

          <div class="list-item list-count-box primary-background-light">
            <div class="list-item-detail">
              <strong>本页合计</strong>
            </div>
            <div class="list-item-detail">
              <span>大课平均人数</span>
              <span>{{ average_attendance }} 人</span>
            </div>
          </div>
        </template>

        <v-empty :is-loading="isLoading" :length="list.length"/>
      </section>
    </template>
  </v-table>
</template>

<script>
import RouteConfig from '@/router/config';
import { mapGetters } from 'vuex';
import { cardTypes } from "@/enums/cardTypes";
import { index, destroy, getQrCode } from '@/api/course';
import { getDay, pickerOptionsOfAfterSystemStartDay, getLegalQueryList } from '@/utils/formats';
import { checkYmdIsSinceSetUp } from '@/utils/validate';
import { dividedBy, formatCourseDate, formatCourseTimeRange } from "@/filters";
import { courseStates, findCourseState } from '@/enums/courseStates';
import { findVideoState } from '@/enums/videoStates';

export default {
  name: RouteConfig.courseList.name,
  data: () => ({
    RouteConfig,
    isLoading: false,
    isDeleting: {},
    isGetting: {},
    count: 0,
    average_attendance: 0,
    qrCode: { src: '' },
    list: [],
    timeLimit: [],
    queryList: {
      store_id: '',
      course_column_id: '',
      teacher_ids: [],
      start_date: '',
      end_date: '',
      is_asc: 0,
      page: 1,
      size: 10,
      card_type_id: 0,
    },
    pickerOptionsOfAfterSystemStartDay,
  }),
  computed: {
    ...mapGetters([
      'isAdministrator',
      'isHeadOffice',
      'isManager',
      'isReceptionist',
      'isMobile',
      'isPc',
      'is2k',
      'userStoreId',
    ]),
  },
  watch: {
    '$route'(to) {
      this.load(to);
    },
    queryList(queryList) {
      if (!this.isMobile) return;
      this.timeLimit = [queryList.start_date, queryList.end_date];
    },
  },
  beforeMount() {
    const today = getDay();
    this.queryList.start_date = today;
    this.queryList.end_date = today;
    this.timeLimit = [this.queryList.start_date, this.queryList.end_date];
    this.load();
  },
  methods: {
    // 出勤状态只显示异常（迟到/早退/请假）：「正常上课」是默认态，每行都挂一个蓝标签纯属噪声，
    // 也让「课程老师/出勤状态」这一列能收窄（见下方 width="380"）
    findAbnormalCourseState(courseStateValue) {
      const [state] = findCourseState(courseStateValue) || [];
      return state && state.value !== courseStates.normal.value ? [state] : [];
    },
    findVideoState,
    formatCourseDate,
    formatCourseTimeRange,
    tableRowClassName({ row }) {
      for(let i = 0; i < row.teachers.length; i++) {
        if (row.teachers[i].sub_teacher_id) return 'warning-background';
      }
      return '';
    },
    refresh(query = this.queryList) {
      RouteConfig.courseList.push(query);
    },
    handlePageChange({ page, size }) {
      this.queryList.page = page;
      this.queryList.size = size;
      this.refresh();
    },
    handleStoreChange() {
      this.queryList.page = 1;
      this.refresh();
    },
    handleTeacherChange() {
      this.queryList.page = 1;
      this.refresh();
    },
    handleTimeLimitChange(times) {
      if (!times || !times.length) times = ['', ''];

      this.timeLimit = times;
      const [start_date, end_date] = times;

      Object.assign(this.queryList, {
        start_date,
        end_date,
        page: 1,
      });
      this.refresh();
    },
    async getQrCode(id, isOpenWindow = false) {
      if (this.isGetting[id]) return;

      this.$set(this.isGetting, id, true);
      const { isError, data } = await getQrCode(id);
      this.$set(this.isGetting, id, false);
      if (isError || !data.qr_code) return;
      if (isOpenWindow) return window.open(data.qr_code);

      this.qr_code = data.qr_code;
    },
    async load(route = this.$route, next) {
      const { queryList } = this;
      const { query } = route;
      getLegalQueryList(queryList, query);

      const startDateIsValid = checkYmdIsSinceSetUp(query.start_date);
      const endDateIsValid = checkYmdIsSinceSetUp(query.end_date);

      if (startDateIsValid && endDateIsValid) {
        queryList.start_date = query.start_date;
        queryList.end_date = query.end_date;
      } else if (next) {
        queryList.start_date = '';
        queryList.end_date = '';
      }

      this.isLoading = true;
      const { isError, data } = await index(queryList);
      this.isLoading = false;

      if (next) next();
      if (isError) return;

      let student_count = 0;
      data.list.forEach(item => {
        if (item.card_type_id === cardTypes.personal.value) {
          item.isNotSummary = true;
        } else {
          student_count += item.student_count;
        }
      });

      this.count = data.count;
      this.average_attendance = dividedBy(student_count, data.list.length);
      this.list = data.list;
    },
    async handleDelete({ id, update_time }) {
      if (!id) return this.$message.warning('缺少课程 ID，无法删除课程！');
      if (!Number.isInteger(update_time) || update_time < 0) return this.$message.error('缺少课程信息，无法删除！');
      if (this.isDeleting[id]) return;

      this.$set(this.isDeleting, id, true);
      const { isError } = await destroy(id, update_time);
      this.$set(this.isDeleting, id, false);
      if (!isError) this.load();
    },
  },
};
</script>
<style src="./index.css" scoped/>
